import React, { useMemo } from 'react'

import classNames from 'classnames'

import { RankedPromoOfferDiscount } from 'src/apollo/onlineOrdering'
import { ButtonType, MenuConfig } from 'src/apollo/sites'
import { formatImageURL, formatPrice } from 'src/lib/js/utilities'
import {
  DEFAULT_IMAGE_SIZE,
  DefaultPlaceHolderMenuImage
} from 'src/public/components/default_template/menu_section/DefaultPlaceHolderMenuImage'
import { MenuItem } from 'src/public/components/default_template/menu_section/MenuSection'
import { CART_POPOVER_CONTEXT_KEY } from 'src/public/components/default_template/online_ordering/cart/CartModal'
import { OutOfStockTag } from 'src/public/components/default_template/online_ordering/item_tags/MenuItemTags'
import { useOOMenus } from 'src/public/components/online_ordering/useOOMenu'
import { BrandedButton } from 'src/shared/components/common/button'
import { useModal } from 'src/shared/components/common/modal'
import { usePopoverContext } from 'src/shared/components/common/popover/PopoverContext'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import { t } from 'src/translations'

import { overrideConfigBasedOnTemplate } from 'public/components/default_template/menu_section/menuStylingOverrides'
import ItemModal from 'public/components/default_template/online_ordering/item_modal/ItemModal'

import {
  getMenuItemFromGuid,
  getOfferPrice,
  getGroupNameFromGuid,
  getMenuNameFromGuid,
  scrollToMenu
} from './OfferUtils'
import { ItemSource } from '../cartUtils'

export type OfferItemsProps = {
  offer: RankedPromoOfferDiscount
  closeOfferItems: () => void
}

export const OfferItemsGrid = ({ offer, closeOfferItems }: OfferItemsProps) => {
  const { menus, menuItems } = useOOMenus({})

  if ((!menus && !menuItems) || !offer?.discountEntities?.items) {
    return null
  }

  const itemList: MenuItem[] = []
  let noImages = true
  offer.discountEntities.items.forEach((guid: string) => {
    const item = getMenuItemFromGuid(guid, menus, menuItems || [])
    if (item) {
      itemList.push(item)
      noImages = noImages && !item.imageUrls?.xs
    }
  })

  return (
    <div className='offerItemsGrid' data-testid='offer-items-grid'>
      {offer.discountEntities.items.map((guid: string) => {
        const item = getMenuItemFromGuid(guid, menus, menuItems || [])
        return (
          <OfferItem
            key={guid}
            item={item}
            offer={offer}
            closeOfferItems={closeOfferItems}
            showImages={!noImages}
          />
        )
      })}
    </div>
  )
}

type OfferItemProps = {
  item?: MenuItem | null
  offer: RankedPromoOfferDiscount
  closeOfferItems: () => void
  showImages: boolean
}
const OfferItem = ({
  item,
  offer,
  closeOfferItems,
  showImages
}: OfferItemProps) => {
  const { isOpen, onClose, onOpen } = useModal()
  const {
    restaurant: { config }
  } = useRestaurant()
  const templateOverrides = useMemo(
    () => overrideConfigBasedOnTemplate(config.ooConfig as MenuConfig, config),
    [config]
  )
  if (!item) {
    return null
  }
  const outOfStock = item.outOfStock
  const price =
    (item.prices?.[0] || item.price || 0) + (item.defaultModifiersPrice || 0)
  const newPrice = getOfferPrice(offer, price)
  let image = null
  if (showImages) {
    if (item?.imageUrls?.xs) {
      image = (
        <>
          <img
            className='imgWrapper'
            src={formatImageURL(item.imageUrls.xs)}
            alt={item.name || ''}
            data-testid='offer-item-image'
          />
          <div className='imgGradient' />
        </>
      )
    } else {
      image = (
        <DefaultPlaceHolderMenuImage
          templateOverrides={templateOverrides}
          imageSize={DEFAULT_IMAGE_SIZE}
          className='imgWrapper'
        />
      )
    }
  }
  return (
    <>
      <ItemModal
        isOpen={isOpen}
        onClose={() => {
          onClose()
          closeOfferItems()
        }}
        itemGuid={item.guid}
        itemGroupGuid={item.itemGroupGuid}
        offer={offer}
        itemSource={ItemSource.CartOffer}
      />
      <button
        className={classNames(
          'offerItem',
          templateOverrides?.borderStroke,
          templateOverrides?.roundedCorner,
          templateOverrides?.dropShadow,
          outOfStock && 'outOfStock'
        )}
        style={{ borderColor: templateOverrides?.borderColor }}
        onClick={onOpen}
        disabled={outOfStock ?? false}
        tabIndex={0}
      >
        <div className='image'>{image}</div>
        <div className='textContent'>
          <div className='titleContainer'>
            <p className='title'>{item.name}</p>
          </div>
          <div className='priceContainer'>
            <p className='price'>{formatPrice(price)}</p>
            {item.outOfStock ? (
              <OutOfStockTag />
            ) : (
              <p className='price free'>
                {newPrice === 0 ? t('offer-items.free') : formatPrice(newPrice)}
              </p>
            )}
          </div>
        </div>
      </button>
    </>
  )
}

export const FullMenuOffer = ({
  onClose
}: {
  onClose: (openCart: boolean) => void
}) => {
  const popovercontext = usePopoverContext(CART_POPOVER_CONTEXT_KEY)

  return (
    <div className='fullMenuOffer'>
      <BrandedButton
        onClick={() => {
          popovercontext?.close()
          onClose(false)
        }}
        variant={ButtonType.Primary}
      >
        {t('offer-items.back-to-menu')}
      </BrandedButton>
    </div>
  )
}

type MenuGroupsListProps = {
  menuGroupGuids: string[]
  menuGuids: string[]
  onClose: (openCart: boolean) => void
}

export const MenuGroupsList = ({
  menuGroupGuids,
  menuGuids,
  onClose
}: MenuGroupsListProps) => {
  const { menus } = useOOMenus({})
  const popovercontext = usePopoverContext(CART_POPOVER_CONTEXT_KEY)

  if (!menus) {
    return null
  }

  // For menu groups: identifier is the GUID, for menus: identifier is the name
  const onMenuClick = (identifier: string, isGroup: boolean = false) => {
    popovercontext?.close()
    onClose(false)
    scrollToMenu(identifier, isGroup)
  }

  return (
    <div>
      <div className='menuGroupHeader'>
        {t('offer-items.eligible-menu-categories')}
      </div>
      <ul className='menuGroupList'>
        {menuGuids.map((menuGuid) => {
          const menuName = getMenuNameFromGuid(menuGuid, menus)
          if (!menuName) {
            return null
          }
          return (
            <li key={menuGuid}>
              {menuName}
              <button onClick={() => onMenuClick(menuGuid, false)}>
                {t('offer-items.view')}
              </button>
            </li>
          )
        })}
        {menuGroupGuids.map((groupGuid) => {
          const groupName = getGroupNameFromGuid(groupGuid, menus)
          if (!groupName) {
            return null
          }
          return (
            <li key={groupGuid}>
              {groupName}
              <button onClick={() => onMenuClick(groupGuid, true)}>
                {t('offer-items.view')}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default OfferItemsGrid
